import React, { useEffect, useMemo } from "react";
import cx from "classnames";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  authenticateUser,
  authenticatingUserSucceeded,
  fetchSSOUrlFromEmail,
  invalidateForgotPassword,
  ssoAuthenticationFailed,
} from "src/redux/actions/users";
import { RootState } from "src/redux/store";

import BrowserService from 'src/utils/browser.service';
import Button from "src/components/button";
import Reveal from "src/components/animation";
import { getPlatformFromDomain } from "src/utils/helper";

import styles from "./login.module.scss";
import { useRecordCapabilityActivation } from "src/query/settings/dashboard";

const SafariPopUp = '/images/popUp/safari_Popup.png';
const FirefoxPopUp = '/images/popUp/firefox_Popup.png';
const ChromePopUp = '/images/popUp/Chrome_Popup.png';
const EdgePopUp = '/images/popUp/Edge_Popup.png';
const DefaultPopUp = '/images/popUp/Default_Popup.png';

function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const passwordInputRef = React.useRef<HTMLInputElement>(null);

  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordText, setShowPasswordText] = React.useState(false);
  const [isPopupBlocked, setIsPopupBlocked] = React.useState(false);
  const { mutate: recordActivation } = useRecordCapabilityActivation();

  const redirectUrl = useMemo(() => {
    let url = searchParams.get("redirect");
    if(!url) {
      url =  "/hub";
    }

    return url;
  }, [searchParams]);

  const authenticating = useSelector(
    (state: RootState) => state.users.authentication?.authenticating,
  );

  const invalidPassword = useSelector((state: RootState) =>
    _.get(state, "users.authentication.error"),
  );
  const authenticated = useSelector((state: RootState) =>
    _.get(state, "users.authentication.authenticated"),
  );

  const ssoUrl = useSelector((state) => _.get(state, "users.sso.url"));
  const ssoError = useSelector((state: RootState) => _.get(state, "users.sso.error"));
  const ssoAuthFailed = useSelector((state: RootState) => _.get(state, "users.sso.ssoAuthFailed.reason"));
  const registrationError = useSelector(
    (state: RootState) =>
      _.get(state, "users.registration.error") as {
        message: string;
      },
  );

  useEffect(() => {
    setUsername("");
    setError({
      username: "",
      password: "",
    });

    window.addEventListener("message", handleCodeRecievedEvent, false);

    return () => {
      window.removeEventListener("message", handleCodeRecievedEvent);
    };
  }, []);

  useEffect(() => {
    if (authenticated) {
      const parentDomain = detectParentDomain();
      if (parentDomain != null) {
        recordActivation(parentDomain);
      }
      if (redirectUrl === "/lite/hub") {
        navigate("/hub");
      } else {
        navigate(redirectUrl);
      }
    }
  }, [authenticated]);

  const detectParentDomain = () => {
      const referrer = document.referrer;
      let domain = "";
      if (referrer) {
        try {
          domain = new URL(referrer).hostname;
        } catch (e) {
          console.log('Could not parse referrer URL');
        }
      }

      return getPlatformFromDomain(domain);
 };

  useEffect(() => {
    if (!_.isEmpty(ssoUrl)) {
      initiateSSO(ssoUrl);
    }
  }, [ssoUrl]);

  useEffect(() => {
    if (ssoError) {
      setShowPassword(true);
    }
  }, [ssoError]);

  useEffect(() => {
    if (ssoAuthFailed) {
      const code = _.get(ssoAuthFailed, "code");
      const redirectUrl = _.get(ssoAuthFailed, "url", "");

      if (code === 20016 && redirectUrl) {
        window.location.assign(redirectUrl);
      } else {
        navigate("/sso/failure");
      }
    }
  }, [ssoAuthFailed]);

  useEffect(() => {
    if (showPassword && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [showPassword]);

  useEffect(() => {
    if (invalidPassword) {
      setError({
        ...error,
        password: invalidPassword.message,
      });
    }
  }, [invalidPassword]);

  useEffect(() => {
    if (registrationError) {
      setError({
        ...error,
        username: registrationError?.message,
      });
    }
  }, [registrationError]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const userNameError = validateUserName();

    if (_.isEmpty(error.username) && !userNameError) {
      if (showPassword) {
        dispatch(authenticateUser({ username, password }));
      } else {
        dispatch(fetchSSOUrlFromEmail({ email: username }));
      }
    }
  };

  const handleUserNameChange = (value: string) => {
    setError({
      ...error,
      username: "",
    });
    setUsername(value);
  };

  const handlePasswordChange = (value: string) => {
    setError({
      ...error,
      password: "",
    });
    setPassword(value.trim());
  };

  const validateUserName = () => {
    if (!username || username.trim() == "") {
      setError({
        ...error,
        username: "Enter a valid email id",
      });

      return true;
    }

    const regex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!regex.test(username)) {
      setError({
        ...error,
        username: "Enter a valid email id",
      });

      return true;
    }

    return false;
  };

  const initiateSSO = (url) => {
    const dualScreenLeft =
      window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop =
      window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
        ? document.documentElement.clientWidth
        : window.screen.width;

    const height = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
        ? document.documentElement.clientHeight
        : window.screen.height;

    const w = 0.8 * width;
    const h = 0.8 * height;

    const left = width / 2 - w / 2 + dualScreenLeft;
    const top = height / 2 - h / 2 + dualScreenTop;

    const windowName = "userConsole";
    const newWindow = window.open(
      url,
      windowName,
      `scrollbars=yes, width=${w}, height=${h}, top=${top}, left=${left}`,
    );

    if (newWindow === null || typeof newWindow === "undefined") {
      console.error("Pop-up blocked or failed to open new window");
      setIsPopupBlocked(true);
    } else {
      newWindow.focus();
    }

    return false;
  };

  const handleCodeRecievedEvent = (event) => {
    if (event.data.type == "SSO_AUTHENTICATION_FAILURE") {
      dispatch(ssoAuthenticationFailed(event.data));
    } else if (event.data.type == "SSO_AUTHENTICATION_SUCCESS") {
      console.log("SSO_AUTHENTICATION_SUCCESS");
      dispatch(authenticatingUserSucceeded(event.data.payload));
    }
  };

  const renderButtonTitle = () => {
    return authenticating ? "Signing In" : "Continue";
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit(event);
    }
  };

  const getPopupImage = useMemo(() => {
    const browser = BrowserService.getCurrentBrowser();
    switch (browser) {
      case 'safari':
        return SafariPopUp;
      case 'firefox':
        return FirefoxPopUp;
      case 'chrome':
        return ChromePopUp;
      case 'edge':
        return EdgePopUp;
      case 'ie':
        return EdgePopUp;
      default:
        return DefaultPopUp;
    }
  }, []);

  return (
    <div className={styles.loginContainer}>
      <img
        src={'/images/Paperflite.png'}
        alt="Paperflite Logo"
        className={styles.logo} 
      />

      {isPopupBlocked && <div className={styles.popupBlocked}>
        <img src={getPopupImage} />
      </div>}

      {!isPopupBlocked && <><h1 className={styles.title}>
        Hello! Sign in to explore Paperflite
      </h1>
        <form
          className={styles.loginForm}
          onSubmit={handleSubmit}
          onKeyDown={handleKeyPress}
        >
          <Reveal show={true} type="fade" delay={2}>
            <input
              className={styles.input}
              type="text"
              placeholder={"Enter your username"}
              value={username}
              onChange={(e) => handleUserNameChange(e.target.value)}
            />
          </Reveal>
          {error.username && (
            <p className={styles.error}>
              <i className="paperflite-danger"></i>
              <label>{error.username}</label>
            </p>
          )}
          <Reveal show={showPassword} type="fade" delay={2}>
            <span className={styles.passwordInput}>
              <input
                ref={passwordInputRef}
                className={styles.input}
                type={showPasswordText ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
              />
              <i
                onClick={() => setShowPasswordText(!showPasswordText)}
                className={cx(
                  showPasswordText ? "paperflite-hidden-eye" : "paperflite-eye",
                )}
              ></i>
            </span>
          </Reveal>
          <div className={styles.forgotPasswordSection}>
            <p
              className={cx(
                _.isEmpty(error.password) ? styles.hideError : styles.error,
              )}
            >
              <i className="paperflite-danger"></i>
              <label>{error.password}</label>
            </p>
            {showPassword && (
              <span
                className={styles.forgotPassword}
                onClick={() => {
                  dispatch(invalidateForgotPassword());
                  navigate("/forgot-password")}
                }
              >
                Forgot Password?
              </span>
            )}
          </div>
          <span onClick={handleSubmit}>
            <Button
              className={styles.button}
              variant="primary"
              icon="paperflite-right-arrow"
              disabled={authenticating}
            >
              {renderButtonTitle()}
            </Button>
          </span>
        </form>
        <p className={styles.signUp}>
          Don&apos;t have an account?
          <span className={styles.signUpText}>
            <a
              href="https://www.paperflite.com/signup?utm_source=Application%20Signup%20Redirect"
              rel="noreferrer"
            >
              Sign up
            </a>
          </span>
        </p>
      </>}
    </div>
  );
}

export default LoginPage;
